// /meet-guest — public entry point for external participants to join a
// PUBLIC meet room without a governance Supabase session. Excluded from
// redirectIfUnauthed via the publicPathPrefixes allowlist in app.js.
//
// Flow (waiting-room-gated):
//  1. User lands on /meet-guest#room=<uuid> (shareable link from a public
//     room's host).
//  2. Page fetches room metadata via a lightweight lookup and shows the
//     required fields (name, maybe email, maybe password) per guest_require_*
//     columns.
//  3. Submit calls meet-guest-request which writes a meet_waiting_room row
//     and returns { waitingId, autoAdmitted }. If guest_auto_admit is on,
//     the page skips directly to minting a token; otherwise it shows a
//     "waiting for host to admit you" spinner and polls meet-guest-status.
//  4. Once admitted, meet-guest-token is called with { roomId, waitingId }
//     and the page connects to LiveKit + renders the call area.
//  5. Rejected → show "access denied" screen with a retry button.
//
// The page does NOT extend AppPage — there's no header, no sidebar, no
// nav. It's a standalone call screen. The intranet's app.js fetches still
// fire on mount but RLS returns empty for anon users, so no crash.

import { RoomEvent } from 'livekit-client'
import { buildLivekitRoom } from '../functions/meet/livekitConnect.js'

const PROJECT_ID = 'becrzpqaiovbvfmrosro'
const PUBLISHABLE_KEY = 'sb_publishable_suMZ-1Crw-304sk_MY-VZA_8aU6ZFfa'

// Guest endpoints are anonymous — the user has not signed in (and may
// never sign in). The guest flow hits three edge functions:
//   - meet-guest-request: submit name/email/password, returns waitingId
//   - meet-guest-status: poll admission state for the host's decision
//   - meet-guest-token: mint LiveKit token after the host admits the guest
//
// Each takes a publishable Supabase key + waitingId — no Bearer JWT.
// The SDK can't carry this flow because every workspace SDK route requires
// an authenticated session. Once a dedicated /workspace/meet-guest/* surface
// is added to the wrapper (with anonymous capability + per-room secret
// validation), this _edgeFetch can be replaced with
// getSDK().execute('workspaceProject.meet.guest', op, payload). For now
// the raw fetch stays — guest auth fundamentally bypasses the SDK auth model.
const _edgeFetch = async (fn, body) => {
  const res = await fetch(`https://${PROJECT_ID}.supabase.co/functions/v1/${fn}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      apikey: PUBLISHABLE_KEY,
      Authorization: `Bearer ${PUBLISHABLE_KEY}`
    },
    body: JSON.stringify(body)
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`)
  return json
}

// Read #room=<uuid> from the URL hash.
const _readRoomFromHash = () => {
  if (typeof window === 'undefined') return null
  const params = new URLSearchParams(window.location.hash.slice(1))
  return params.get('room')
}

// Module-level live Room — state can't hold class instances with methods.
let _guestRoom = null
let _pollTimer = null

const _stopPolling = () => {
  if (_pollTimer) { clearInterval(_pollTimer); _pollTimer = null }
}

const _refreshGuestParticipants = (root, livekitRoom) => {
  const local = livekitRoom.localParticipant
  const remoteValues = typeof livekitRoom.remoteParticipants?.values === 'function'
    ? livekitRoom.remoteParticipants.values()
    : Object.values(livekitRoom.remoteParticipants || {})
  const remote = Array.from(remoteValues).filter((p) => typeof p.identity === 'string' && !p.identity.startsWith('agent-'))
  root.update({
    livekitParticipants: [
      {
        identity: local.identity,
        name: local.name || local.identity,
        isLocal: true,
        isMicEnabled: local.isMicrophoneEnabled,
        isCameraEnabled: local.isCameraEnabled
      },
      ...remote.map((p) => ({
        identity: p.identity,
        name: p.name || p.identity,
        isLocal: false,
        isMicEnabled: p.isMicrophoneEnabled,
        isCameraEnabled: p.isCameraEnabled
      }))
    ]
  })
}

const _connectGuest = async ({ roomId, waitingId, root }) => {
  try {
    const tokenInfo = await _edgeFetch('meet-guest-token', { roomId, waitingId })
    const livekitRoom = buildLivekitRoom(1)
    const refresh = () => _refreshGuestParticipants(root, livekitRoom)
    livekitRoom.on(RoomEvent.ParticipantConnected, refresh)
    livekitRoom.on(RoomEvent.ParticipantDisconnected, refresh)
    livekitRoom.on(RoomEvent.TrackSubscribed, refresh)
    livekitRoom.on(RoomEvent.TrackUnsubscribed, refresh)
    livekitRoom.on(RoomEvent.TrackMuted, refresh)
    livekitRoom.on(RoomEvent.TrackUnmuted, refresh)
    livekitRoom.on(RoomEvent.LocalTrackPublished, refresh)
    livekitRoom.on(RoomEvent.LocalTrackUnpublished, refresh)
    livekitRoom.on(RoomEvent.Disconnected, () => {
      _guestRoom = null
      root.update({ guestConnected: false, livekitParticipants: [] })
    })
    await livekitRoom.connect(tokenInfo.livekitUrl, tokenInfo.livekitToken)
    try { await livekitRoom.localParticipant.setMicrophoneEnabled(true) } catch (err) { console.warn('mic', err) }
    try { await livekitRoom.localParticipant.setCameraEnabled(true) } catch (err) { console.warn('cam', err) }
    _guestRoom = livekitRoom
    root.update({ guestStatus: 'connected', guestConnected: true, guestError: null })
    _refreshGuestParticipants(root, livekitRoom)
  } catch (err) {
    console.error('[guest] connect failed', err)
    root.update({ guestError: err?.message || 'connect failed', guestStatus: 'form' })
  }
}

const _startPolling = ({ waitingId, roomId, root }) => {
  _stopPolling()
  _pollTimer = setInterval(async () => {
    try {
      const res = await _edgeFetch('meet-guest-status', { waitingId })
      if (res.status === 'admitted') {
        _stopPolling()
        root.update({ guestStatus: 'connecting' })
        await _connectGuest({ roomId, waitingId, root })
      } else if (res.status === 'rejected') {
        _stopPolling()
        root.update({ guestStatus: 'rejected' })
      }
    } catch (err) {
      console.warn('[guest] status poll failed', err)
    }
  }, 2000)
}

const _submitRequest = async ({ root }) => {
  const draft = root.guestForm || {}
  const roomId = root.activeRoomId
  if (!roomId) return
  root.update({ guestError: null, guestStatus: 'submitting' })
  try {
    const res = await _edgeFetch('meet-guest-request', {
      roomId,
      guestName: draft.name,
      guestEmail: draft.email,
      password: draft.password
    })
    if (res.autoAdmitted) {
      root.update({ guestStatus: 'connecting', waitingId: res.waitingId })
      await _connectGuest({ roomId, waitingId: res.waitingId, root })
    } else {
      root.update({ guestStatus: 'waiting', waitingId: res.waitingId })
      _startPolling({ waitingId: res.waitingId, roomId, root })
    }
  } catch (err) {
    console.error('[guest] submit failed', err)
    root.update({ guestError: err?.message || 'submit failed', guestStatus: 'form' })
  }
}

// Lookup the room's guest requirements so we know which form fields to
// show. Uses the anon key to hit the REST endpoint directly — the row is
// readable via the meet_rooms_select RLS policy's public branch.
const _loadRoomMeta = async ({ roomId, root }) => {
  try {
    const res = await fetch(
      `https://${PROJECT_ID}.supabase.co/rest/v1/meet_rooms?id=eq.${roomId}&select=name,is_private,ended_at,guest_require_name,guest_require_email,guest_password,guest_auto_admit`,
      {
        headers: {
          apikey: PUBLISHABLE_KEY,
          Authorization: `Bearer ${PUBLISHABLE_KEY}`
        }
      }
    )
    const rows = await res.json().catch(() => [])
    const row = Array.isArray(rows) ? rows[0] : null
    if (!row) {
      root.update({ guestError: 'Room not found or not public.', guestStatus: 'form' })
      return
    }
    root.update({
      guestRoomMeta: {
        name: row.name || 'Call',
        isPrivate: !!row.is_private,
        endedAt: row.ended_at,
        requireName: row.guest_require_name !== false,
        requireEmail: !!row.guest_require_email,
        requirePassword: !!row.guest_password,
        autoAdmit: !!row.guest_auto_admit
      }
    })
  } catch (err) {
    console.warn('[guest] room meta lookup failed', err)
    root.update({ guestError: 'Room lookup failed.', guestStatus: 'form' })
  }
}

export const getGuestRoom = () => _guestRoom

export const guest = {
  extends: 'Page',
  metadata: { title: 'Join meet / Symbols Workspace' },
  display: 'flex',
  flexFlow: 'column',
  width: '100%',
  minHeight: '100dvh',
  background: 'document',
  color: 'title',

  onCreate: (el, s) => {
    const roomId = _readRoomFromHash()
    s.root.update({
      activeRoomId: roomId,
      guestConnected: false,
      guestError: null,
      guestStatus: 'form',
      guestForm: { name: '', email: '', password: '' },
      guestRoomMeta: null,
      waitingId: null
    })
    if (roomId) _loadRoomMeta({ roomId, root: s.root })
  },

  Container: {
    flex: '1 1 0',
    flow: 'y',
    align: 'center center',
    padding: 'C',
    gap: 'B',

    // ── Form state ────────────────────────────────────────────────
    JoinForm: {
      hide: (el, s) => s.root?.guestStatus !== 'form' && s.root?.guestStatus !== 'submitting',
      flow: 'y',
      gap: 'A',
      padding: 'B',
      round: 'A',
      maxWidth: 'I',
      width: '100%',
      borderWidth: '1px',
      borderColor: 'line',
      borderStyle: 'solid',
      background: 'overlayStrong',

      Title: {
        tag: 'h1',
        fontSize: 'C',
        fontWeight: '700',
        margin: '0',
        color: 'title',
        text: (el, s) => s.root?.guestRoomMeta?.name || 'Join call as guest'
      },
      Subtitle: {
        tag: 'p',
        margin: '0',
        fontSize: 'Z2',
        color: 'caption',
        text: (el, s) => {
          if (!s.root?.activeRoomId) return 'Missing room id — check the share link.'
          const meta = s.root?.guestRoomMeta
          if (!meta) return 'Loading…'
          if (meta.endedAt) return 'This call has ended.'
          return meta.autoAdmit
            ? 'Fill in your details to join directly.'
            : 'Fill in your details. The host will need to admit you.'
        }
      },

      NameField: {
        hide: (el, s) => !s.root?.activeRoomId || !s.root?.guestRoomMeta?.requireName,
        flow: 'y',
        gap: 'V',

        Label: { tag: 'span', fontSize: 'Y2', color: 'caption', text: 'YOUR NAME' },
        Input: {
          tag: 'input',
          padding: 'Z A',
          round: 'Z',
          borderWidth: '1px',
          borderColor: 'line',
          borderStyle: 'solid',
          background: 'document',
          color: 'title',
          fontSize: 'Z2',
          placeholder: 'e.g. Jane Doe',
          value: (el, s) => s.root?.guestForm?.name || '',
          onInput: (e, el, s) => s.root?.update({
            guestForm: { ...(s.root?.guestForm || {}), name: e.target.value }
          })
        }
      },

      EmailField: {
        hide: (el, s) => !s.root?.activeRoomId || !s.root?.guestRoomMeta?.requireEmail,
        flow: 'y',
        gap: 'V',

        Label: { tag: 'span', fontSize: 'Y2', color: 'caption', text: 'EMAIL' },
        Input: {
          tag: 'input',
          type: 'email',
          padding: 'Z A',
          round: 'Z',
          borderWidth: '1px',
          borderColor: 'line',
          borderStyle: 'solid',
          background: 'document',
          color: 'title',
          fontSize: 'Z2',
          placeholder: 'jane@example.com',
          value: (el, s) => s.root?.guestForm?.email || '',
          onInput: (e, el, s) => s.root?.update({
            guestForm: { ...(s.root?.guestForm || {}), email: e.target.value }
          })
        }
      },

      PasswordField: {
        hide: (el, s) => !s.root?.activeRoomId || !s.root?.guestRoomMeta?.requirePassword,
        flow: 'y',
        gap: 'V',

        Label: { tag: 'span', fontSize: 'Y2', color: 'caption', text: 'PASSWORD' },
        Input: {
          tag: 'input',
          type: 'password',
          padding: 'Z A',
          round: 'Z',
          borderWidth: '1px',
          borderColor: 'line',
          borderStyle: 'solid',
          background: 'document',
          color: 'title',
          fontSize: 'Z2',
          placeholder: 'Room password',
          value: (el, s) => s.root?.guestForm?.password || '',
          onInput: (e, el, s) => s.root?.update({
            guestForm: { ...(s.root?.guestForm || {}), password: e.target.value }
          })
        }
      },

      ErrorMsg: {
        hide: (el, s) => !s.root?.guestError,
        tag: 'p',
        margin: '0',
        padding: 'Z A',
        round: 'Z',
        background: 'red',
        color: 'white',
        fontSize: 'Y2',
        text: (el, s) => s.root?.guestError || ''
      },

      JoinBtn: {
        tag: 'button',
        padding: 'Z B',
        round: 'Z',
        border: 'none',
        theme: 'primary',
        cursor: 'pointer',
        fontSize: 'Z2',
        fontWeight: '600',
        text: (el, s) => {
          if (s.root?.guestStatus === 'submitting') return 'Requesting…'
          return s.root?.guestRoomMeta?.autoAdmit ? 'Join call' : 'Request to join'
        },
        disabled: (el, s) => {
          if (s.root?.guestStatus === 'submitting') return true
          if (!s.root?.activeRoomId) return true
          const meta = s.root?.guestRoomMeta
          if (!meta) return true
          if (meta.endedAt) return true
          const draft = s.root?.guestForm || {}
          if (meta.requireName && !(draft.name || '').trim()) return true
          if (meta.requireEmail && !(draft.email || '').trim()) return true
          if (meta.requirePassword && !(draft.password || '').trim()) return true
          return false
        },
        onClick: (e, el, s) => _submitRequest({ root: s.root })
      }
    },

    // ── Waiting state ─────────────────────────────────────────────
    WaitingScreen: {
      hide: (el, s) => s.root?.guestStatus !== 'waiting' && s.root?.guestStatus !== 'connecting',
      flow: 'y',
      gap: 'A',
      padding: 'B',
      round: 'A',
      maxWidth: 'I',
      width: '100%',
      borderWidth: '1px',
      borderColor: 'line',
      borderStyle: 'solid',
      background: 'overlayStrong',
      align: 'center center',

      Spinner: { tag: 'span', fontSize: 'C', text: '⏳' },
      Title: {
        tag: 'h2',
        fontSize: 'B',
        fontWeight: '600',
        margin: '0',
        color: 'title',
        text: (el, s) => s.root?.guestStatus === 'connecting'
          ? 'Admitted — connecting…'
          : 'Waiting for the host to let you in'
      },
      Subtitle: {
        tag: 'p',
        margin: '0',
        fontSize: 'Z2',
        color: 'caption',
        text: 'This usually takes a few seconds. Leave this tab open.'
      }
    },

    // ── Rejected ──────────────────────────────────────────────────
    RejectedScreen: {
      hide: (el, s) => s.root?.guestStatus !== 'rejected',
      flow: 'y',
      gap: 'A',
      padding: 'B',
      round: 'A',
      maxWidth: 'I',
      width: '100%',
      borderWidth: '1px',
      borderColor: 'red',
      borderStyle: 'solid',
      background: 'overlayStrong',
      align: 'center center',

      Icon: { tag: 'span', fontSize: 'C', text: '⛔' },
      Title: {
        tag: 'h2',
        fontSize: 'B',
        fontWeight: '600',
        margin: '0',
        color: 'title',
        text: 'The host declined your request'
      },
      RetryBtn: {
        tag: 'button',
        text: 'Try again',
        padding: 'Z B',
        round: 'Z',
        border: 'none',
        theme: 'secondary',
        cursor: 'pointer',
        fontSize: 'Z2',
        fontWeight: '600',
        onClick: (e, el, s) => {
          _stopPolling()
          s.root?.update({ guestStatus: 'form', waitingId: null, guestError: null })
        }
      }
    },

    // ── Connected — video call area ───────────────────────────────
    CallArea: {
      hide: (el, s) => s.root?.guestStatus !== 'connected',
      flow: 'y',
      flex: '1 1 0',
      minHeight: '0',
      maxWidth: 'J',
      width: '100%',
      background: 'black',
      round: 'A',
      overflow: 'hidden',
      position: 'relative',

      onRender: (el) => {
        const node = el.node
        if (!node) return
        const videoEl = node.querySelector('video')
        if (!videoEl) return
        const room = _guestRoom
        if (!room) return
        const pubs = typeof room.localParticipant?.videoTrackPublications?.values === 'function'
          ? [...room.localParticipant.videoTrackPublications.values()]
          : Object.values(room.localParticipant?.videoTrackPublications || {})
        let targetPub = null
        for (const pub of pubs) {
          if (pub.track && (pub.source === 'camera' || pub.kind === 'video')) { targetPub = pub; break }
        }
        const currentSid = videoEl._lkTrackSid || null
        const targetSid = targetPub?.trackSid || null
        if (currentSid === targetSid) return
        if (videoEl._lkTrack) { try { videoEl._lkTrack.detach(videoEl) } catch { /* ignore */ } }
        if (targetPub?.track) {
          try {
            targetPub.track.attach(videoEl)
            videoEl._lkTrack = targetPub.track
            videoEl._lkTrackSid = targetSid
          } catch { /* ignore */ }
        }
      },

      VideoEl: {
        tag: 'video',
        width: '100%',
        height: '100%',
        minHeight: 'H',
        objectFit: 'cover',
        attr: { autoplay: 'autoplay', playsinline: 'playsinline', muted: 'muted' }
      },

      ControlRow: {
        position: 'absolute',
        bottom: 'A',
        left: '0',
        right: '0',
        flow: 'x',
        align: 'center center',
        gap: 'A',

        LeaveBtn: {
          tag: 'button',
          text: 'Leave call',
          padding: 'Z B',
          round: 'Z',
          border: 'none',
          cursor: 'pointer',
          fontSize: 'Z2',
          fontWeight: '600',
          background: 'red',
          color: 'white',
          onClick: async (e, el, s) => {
            _stopPolling()
            if (_guestRoom) {
              try { await _guestRoom.disconnect() } catch { /* ignore */ }
              _guestRoom = null
            }
            s.root?.update({
              guestConnected: false,
              guestStatus: 'form',
              livekitParticipants: [],
              waitingId: null
            })
          }
        }
      }
    }
  }
}
